// Pure helpers that turn raw scraper / parser / API data into the segments of the two
// pipeline mini-bars. Rules mirror Document Trace's `splitDiagnosis` so both screens agree.

export const COLORS = {
  ok: '#17c671',
  fail: '#c4183c',
  warning: '#ffb400',
  pending: '#d1d6dc'
};

const segment = (color, title) => ({ color, title });

export const safeJson = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const versionOf = (doc) => [doc.majorVersion, doc.minorVersion, doc.patchVersion].map(Number);

const compareVersions = (a, b) => {
  const va = versionOf(a);
  const vb = versionOf(b);
  for (let i = 0; i < va.length; i += 1) {
    if (va[i] !== vb[i]) return va[i] - vb[i];
  }
  return 0;
};

const versionLabel = (doc) => `v${doc.majorVersion}.${doc.minorVersion}.${doc.patchVersion}`;

export const groupBy = (collection, key) =>
  (collection || []).reduce((memo, item) => {
    const value = item[key];
    memo[value] = [...(memo[value] || []), item];
    return memo;
  }, {});

// A reparsed document has parser docs from several versions; keep only the highest one.
export const latestVersionDocs = (parserDocs) => {
  if (!parserDocs || !parserDocs.length) return [];
  const latest = parserDocs.reduce((max, doc) => (compareVersions(doc, max) > 0 ? doc : max));
  return parserDocs
    .filter((doc) => compareVersions(doc, latest) === 0)
    .sort((a, b) => Number(a.split) - Number(b.split));
};

const apiResult = (partResult) => partResult?.api_result || partResult?.apiResult;

// `setDocumentStatus(ok)` clears parser/API keys and leaves `{ ok: 1, total: 1 }` + parsed.
export const isManuallyClosed = (scraperDoc) => {
  const status = safeJson(scraperDoc.processStatus);
  return Boolean(
    scraperDoc.parsed &&
    status &&
    status.ok === 1 &&
    status.total === 1 &&
    !Array.isArray(status.processed_parts)
  );
};

// Bar 1: scraper doc -> parser docs (one segment per split of the latest version).
export const parserSegments = (scraperDoc, parserDocs, parserFails) => {
  if (parserDocs === null || parserFails === null) return null;

  const docs = latestVersionDocs(parserDocs);
  const fails = parserFails || [];
  const segments = [
    ...docs.map((doc) =>
      segment(COLORS.ok, `Split ${doc.split} · ${versionLabel(doc)} · ${doc.id}`)
    ),
    ...fails.map((fail) =>
      segment(
        COLORS.fail,
        `Parser fail · ${fail.exceptionClass || ''} ${fail.debugMessage || ''}`.trim()
      )
    )
  ];

  if (!segments.length) {
    return [
      segment(
        COLORS.pending,
        `Sin parsear / pendiente · status scraper: ${scraperDoc.status || '-'}`
      )
    ];
  }
  return segments;
};

const apiSegmentFor = (parserDoc, payments, fails, partResult, processed) => {
  if (fails.length) {
    const fail = fails[0];
    return segment(COLORS.fail, `API fail · ${fail.debugMessage || fail.moreInfo || parserDoc.id}`);
  }
  if (payments.some((payment) => String(payment.isBalanced) === 'false')) {
    return segment(COLORS.warning, `Payment no balancea · split ${parserDoc.split}`);
  }
  if (payments.length) return segment(COLORS.ok, `Payment creado · split ${parserDoc.split}`);
  if (apiResult(partResult) === 'already_processed') {
    return segment(COLORS.ok, `Ya procesado en API · split ${parserDoc.split}`);
  }
  if (processed.includes(parserDoc.id)) {
    return segment(COLORS.pending, `Callback sin payment · split ${parserDoc.split}`);
  }
  return segment(COLORS.pending, `Sin callback API · split ${parserDoc.split}`);
};

// Bar 2: parser doc -> payment (one to one, latest parser version only).
export const apiSegments = (scraperDoc, parserDocs, payments, paymentFails) => {
  if (parserDocs === null || payments === null || paymentFails === null) return null;

  if (isManuallyClosed(scraperDoc)) return [segment(COLORS.ok, 'Cerrado manualmente')];

  const status = safeJson(scraperDoc.processStatus) || {};
  const processed = Array.isArray(status.processed_parts) ? status.processed_parts : [];
  const partResults = status.processed_part_results || {};
  const paymentsByParsed = groupBy(payments, 'parsedDocumentExtId');
  const failsByParsed = groupBy(paymentFails, 'parsedDocumentExtId');

  const docs = latestVersionDocs(parserDocs);
  const segments = docs.map((doc) =>
    apiSegmentFor(
      doc,
      paymentsByParsed[doc.id] || [],
      failsByParsed[doc.id] || [],
      partResults[doc.id],
      processed
    )
  );

  // Pad with the parts API still expects but no parser doc covers.
  const expected = Number(status.expected_total) || 0;
  for (let i = segments.length; i < expected; i += 1) {
    segments.push(segment(COLORS.pending, `Parte ${i + 1}/${expected} faltante`));
  }

  if (!segments.length) return [segment(COLORS.pending, 'Sin parser docs para enviar a API')];
  return segments;
};
